import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function ConfirmEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const token = searchParams.get("token");
    const urlStatus = searchParams.get("status");
    const urlMsg = searchParams.get("msg");

    // New flow: email link directly hits this frontend page with a `token`.
    // We call the JSON API to validate it (no /api/... in the email).
    if (token) {
      (async () => {
        try {
          await api.post("/auth/confirm-email", { token });
          setStatus("success");
          setMessage("Email confermata con successo!");
          await checkAuth();
        } catch (e) {
          setStatus("error");
          setMessage(e.message || "Token non valido o scaduto.");
        }
      })();
      return;
    }

    // Legacy flow: backend already redirected here with ?status=success|error
    if (urlStatus === "success") {
      setStatus("success");
      setMessage("Email confermata con successo!");
      checkAuth();
    } else if (urlStatus === "error") {
      setStatus("error");
      setMessage(urlMsg || "Errore nella conferma.");
    } else {
      setStatus("error");
      setMessage("Parametri mancanti.");
    }
  }, [searchParams, checkAuth]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white/[0.07] backdrop-blur-xl border border-white/10 shadow-2xl">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            <div className="h-14 w-14 bg-[#c8e414] rounded-full flex items-center justify-center shadow-lg">
              <Trophy className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase">Conferma Email</h1>
          {status === "loading" && (
            <div className="py-6">
              <Loader2 className="h-10 w-10 animate-spin text-[#c8e414] mx-auto" />
              <p className="text-white/60 mt-4 text-sm">Conferma in corso...</p>
            </div>
          )}
          {status === "success" && (
            <div className="py-4 space-y-4">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto" />
              <p className="text-white text-base" data-testid="confirm-success-msg">{message}</p>
              <Button onClick={() => navigate("/dashboard")} className="bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase rounded-sm" data-testid="confirm-go-dashboard">
                Vai alla Dashboard
              </Button>
            </div>
          )}
          {status === "error" && (
            <div className="py-4 space-y-4">
              <XCircle className="h-12 w-12 text-red-400 mx-auto" />
              <p className="text-white text-base" data-testid="confirm-error-msg">{message}</p>
              <Button onClick={() => navigate("/")} variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm" data-testid="confirm-go-home">
                Torna alla Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
